import React, { useState, useEffect } from 'react';
import { Search, Eye, Ban, CheckCircle, ChevronLeft, ChevronRight, UserPlus, X, Gift } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { referralAdminService } from '../../services/referralAdminService';
import { useNavigate } from 'react-router-dom';
import Toast from '../../components/Toast';
import { useTranslation } from '../../i18n/LocaleContext';
import SetBonusOrderModal from '../../components/SetBonusOrderModal';

const AdminUsers = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [users, setUsers] = useState([]);

  // Check user type
  const userStr = localStorage.getItem('user');
  const currentUser = userStr ? JSON.parse(userStr) : null;
  const isReferralAdmin = currentUser?.userType === 'referral_admin';
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0 });
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', phone: '', password: '' });
  const [showBonusModal, setShowBonusModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [pagination.page, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      // Use different service based on user type
      const service = isReferralAdmin ? referralAdminService : adminService;
      const data = await service.getUsers(pagination.page, pagination.limit, searchTerm);
      setUsers(data.users || []);
      setStats(data.stats);
      setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) {
      console.error('Fetch users error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getStatusBadge = (active) => {
    return Number(active) === 1 ? (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-green-900 bg-opacity-30 border border-green-700 text-green-400">
        <CheckCircle className="w-3 h-3" />
        {t('aUsersStatusActive')}
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-900 bg-opacity-30 border border-red-700 text-red-400">
        <Ban className="w-3 h-3" />
        {t('aUsersStatusLocked')}
      </span>
    );
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!newUser.username || !newUser.phone || !newUser.password) {
      setToast({ type: 'error', message: t('fillAllFields') });
      return;
    }
    try {
      setCreating(true);
      await adminService.createUser(newUser);
      setToast({ type: 'success', message: t('userCreated') });
      setShowCreateModal(false);
      setNewUser({ username: '', phone: '', password: '' });
      fetchUsers();
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('aUsersTitle')}</h1>
          <p className="text-gray-400 mt-1">{t('aUsersSubtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg font-medium hover:bg-brand-600 transition-colors"
        >
          <UserPlus className="w-5 h-5" />
          {t('aUsersCreateAccount')}
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
            <p className="text-gray-400 text-sm">{t('aUsersStatTotal')}</p>
            <p className="text-2xl font-bold text-white">{stats.total_users || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
            <p className="text-gray-400 text-sm">{t('aUsersStatActive')}</p>
            <p className="text-2xl font-bold text-white">{stats.active_users || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
            <p className="text-gray-400 text-sm">{t('aUsersStatLocked')}</p>
            <p className="text-2xl font-bold text-white">{stats.locked_users || 0}</p>
          </div>
          <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
            <p className="text-gray-400 text-sm">{t('aUsersStatNewToday')}</p>
            <p className="text-2xl font-bold text-white">{stats.new_today || 0}</p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t('aUsersSearchPlaceholder')}
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">VIP</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThBalance')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThOrders')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThToday')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThReferrer')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThReferralCode')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThJoined')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aUsersThActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-400">
                    {t('aUsersLoading')}
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan="10" className="px-6 py-8 text-center text-gray-400">
                    {t('aUsersEmpty')}
                  </td>
                </tr>
              ) : users.map((user) => (
                <tr key={user.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{user.id}</td>
                  <td className="px-6 py-4">
                    <div>
                      <button
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        className="text-sm font-medium text-white hover:text-brand-400 transition-colors text-left"
                      >
                        {user.username}
                      </button>
                      <p className="text-xs text-gray-400">{user.email || user.phone}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-300">
                      {String(user.vip || '').toUpperCase().startsWith('VIP') ? user.vip : `VIP ${user.vip || 0}`}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm font-semibold text-white">{formatCurrency(user.balance)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{user.completed_orders || 0}</td>
                  <td className="px-6 py-4">
                    {user.daily_orders_completed !== undefined && user.daily_orders_total !== undefined ? (
                      <span className={`text-sm font-medium ${
                        user.daily_orders_completed === user.daily_orders_total
                          ? 'text-green-400'
                          : 'text-yellow-400'
                      }`}>
                        {user.daily_orders_completed}/{user.daily_orders_total}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.referrer_username ? (
                      <span className="text-sm text-gray-300">
                        {user.referrer_username}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-500">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {user.referral_code ? (
                      <span className="text-sm text-gray-300 font-mono">
                        {user.referral_code}
                      </span>
                    ) : (
                      <button
                        onClick={async () => {
                          if (!window.confirm(t('aUsersGenerateCodeConfirm', { name: user.username }))) {
                            return;
                          }
                          try {
                            const response = await adminService.generateReferralCode(user.id);
                            setToast({ type: 'success', message: response.message });
                            fetchUsers();
                          } catch (error) {
                            setToast({ type: 'error', message: error.message });
                          }
                        }}
                        className="px-2 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs font-medium"
                      >
                        {t('aUsersGenerateCode')}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(user.active)}</td>
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(user.created_at).toLocaleDateString('vi-VN')}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded-lg text-xs hover:bg-gray-600"
                      >
                        <Eye className="w-3 h-3" />
                        {t('aUsersView')}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser(user);
                          setShowBonusModal(true);
                        }}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-gray-700 text-white rounded-lg text-xs hover:bg-gray-600"
                        title="Set Bonus Order"
                      >
                        <Gift className="w-3 h-3" />
                        Bonus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="bg-gray-900 px-6 py-4 flex items-center justify-between border-t border-gray-700">
            <div className="text-sm text-gray-400">
              {t('aUsersShowing', { from: ((pagination.page - 1) * pagination.limit) + 1, to: Math.min(pagination.page * pagination.limit, pagination.total), total: pagination.total })}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                {t('aUsersPrev')}
              </button>
              <div className="flex items-center gap-1">
                {[...Array(pagination.total_pages)].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: i + 1 }))}
                    className={`px-3 py-1 rounded-lg text-sm ${
                      pagination.page === i + 1
                        ? 'bg-brand-500 text-white font-medium'
                        : 'bg-gray-700 text-white hover:bg-gray-600'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.total_pages}
                className="px-3 py-1 bg-gray-700 text-white rounded-lg text-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                {t('aUsersNext')}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-md rounded-2xl border border-gray-700 bg-gray-800 p-6 shadow-2xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white">{t('aUsersCreateTitle')}</h2>
                <p className="mt-1 text-sm text-gray-400">{t('aUsersCreateSubtitle')}</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                disabled={creating}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-700 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">{t('aUsersLabelUsername')}</label>
                <input
                  type="text"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  placeholder={t('aUsersPlaceholderUsername')}
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  disabled={creating}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">{t('aUsersLabelPhone')}</label>
                <input
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                  placeholder={t('aUsersPlaceholderPhone')}
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  disabled={creating}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-300">{t('aUsersLabelPassword')}</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder={t('aUsersPlaceholderPassword')}
                  className="w-full rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 text-white outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                  disabled={creating}
                />
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                  className="rounded-lg bg-gray-700 px-4 py-2 font-medium text-white hover:bg-gray-600 disabled:opacity-50"
                >
                  {t('aUsersCancel')}
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="rounded-lg bg-brand-500 px-4 py-2 font-medium text-white hover:bg-brand-600 disabled:opacity-50"
                >
                  {creating ? t('aUsersCreating') : t('aUsersCreateAccount')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Set Bonus Order Modal */}
      <SetBonusOrderModal
        isOpen={showBonusModal}
        onClose={() => {
          setShowBonusModal(false);
          setSelectedUser(null);
          fetchUsers(); // Refresh users list
        }}
        userId={selectedUser?.id}
        userName={selectedUser?.username}
      />
    </div>
  );
};

export default AdminUsers;
